import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import posthog from 'posthog-js'
import classNames from 'classnames'
import moment from 'jalali-moment'
import { toast } from 'react-toastify'
import { AnimatePresence, motion } from 'framer-motion'
import { FiChevronLeft } from 'react-icons/fi'
import {
    LuInbox,
    LuMessagesSquare,
    LuSearchX,
    LuTrash2,
    LuUserPlus,
} from 'react-icons/lu'
import Header from 'components/headers/funds'
import SideBar from 'components/sidebar'
import SideBarItems from 'components/sidebar/items'
import CustomBottomSheet from 'components/bottomSheet'
import { SectionError } from 'components/states'
import { getBoxes } from 'services/funds'
import { deleteMessage, makeMessagesSeen } from 'services/messages'
import {
    UNSEEN_COUNT_KEY,
    badgeText,
    markSeenInCache,
    removeFromCache,
} from 'modules/inbox'
import { faPlain } from 'utilities/helper/format'
import useInbox, { useLoanThreads } from './hooks/useInbox'
import useGetCashboxMessages from './hooks/useGetCashboxMessages'
import MessageCard, { MessageCardSkeleton } from './item'
import { LoanThreads, threadPath } from './loanThreads'
import { FilterBar, FilterSummary } from './filters'
import { isInboxCategory, isInboxPeriod } from './categories'
import {
    MessageTarget,
    groupByDay,
    localDayKey,
    resolveMessageTarget,
} from './messageTarget'

/** Android marks the inbox seen a second after the tab opens; so do we. */
const MARK_SEEN_DELAY_MS = 1200

type Tab = 'inbox' | 'loans'

const dayLabel = (key: string) => {
    const now = Math.floor(Date.now() / 1000)
    if (key === localDayKey(now)) return 'امروز'
    if (key === localDayKey(now - 86_400)) return 'دیروز'
    const day = moment.from(key, 'en', 'YYYY-MM-DD').locale('fa')
    const sameYear =
        day.format('jYYYY') === moment().locale('fa').format('jYYYY')
    return day.format(sameYear ? 'dddd D MMMM' : 'dddd D MMMM YYYY')
}

/**
 * «پیام‌ها» — the member's inbox.
 *
 * Every message used to be a grey-on-white paragraph with an `HH:mm:ss` stamp,
 * and tapping one did nothing: the PWA never implemented Android's `doAction`.
 * A reminder to pay could not take you to the payment, and a message could not
 * be deleted.
 *
 * Now a card says which fund it is about and what kind of message it is, goes
 * where Android's tap went (resolved against the member's own funds, see
 * `messageTarget.ts`), and can be deleted. On top of that:
 *
 * - the inbox filters by time («یک روز» / «یک هفته» / «یک ماه») and by kind,
 *   server-side, each combination its own cache entry so going back to one is
 *   instant. The filter lives in the URL, so a reload — or a shared link —
 *   keeps it;
 * - loan conversations get their own tab, because a back-and-forth with the
 *   fund's manager reads as a thread, not as loose messages;
 * - read state is the server's, not this device's: a yellow square turns into a
 *   green tick here when the message is read on the phone, and the footer badge
 *   follows the same number.
 *
 * `['get-boxes']` is shared with the home page, so resolving fund names costs
 * no extra request for anyone who came from there.
 */
export default function MessagesPage() {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [params, setParams] = useSearchParams()

    const tab: Tab = params.get('tab') === 'loans' ? 'loans' : 'inbox'
    const periodParam = params.get('period')
    const categoryParam = params.get('category')
    const period = isInboxPeriod(periodParam) ? periodParam : undefined
    const category = isInboxCategory(categoryParam) ? categoryParam : undefined

    const {
        data,
        isLoading,
        isError,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInbox(period, category)
    const threads = useLoanThreads()
    const { cashboxData } = useGetCashboxMessages()
    const { data: boxes } = useQuery(['get-boxes'], getBoxes, {
        staleTime: 60_000,
    })

    const [hidden, setHidden] = useState<ReadonlySet<number>>(() => new Set())
    const [menuFor, setMenuFor] = useState<IInboxMessage | null>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const days = useMemo(
        () =>
            groupByDay(
                data?.pages.flatMap((page) => page.results),
                hidden
            ),
        [data, hidden]
    )
    const messages = useMemo(() => days.flatMap((day) => day.messages), [days])

    // Everything that arrived unseen stays highlighted for this visit, even
    // after the server (and the cache) have marked it seen. Adding ids is
    // idempotent, so doing it while rendering is safe.
    const freshRef = useRef(new Set<number>())
    for (const message of messages) {
        if (!message.is_seen) freshRef.current.add(message.id)
    }

    const targets = useMemo(() => {
        const map = new Map<number, MessageTarget>()
        for (const message of messages)
            map.set(message.id, resolveMessageTarget(message, boxes))
        return map
    }, [messages, boxes])

    // «مدیران» holds manager notes and @mentions, which only a fund's owner or
    // manager is ever sent: for anyone else the chip could only open an empty
    // list. It stays while it is the active filter (a restored URL), so a chip
    // never disappears from under the member's finger.
    const managesAFund = Boolean(
        boxes?.moneypools?.some((fund) =>
            ['own', 'mgr'].includes(fund.poolship?.role)
        )
    )
    const hiddenCategories: TInboxCategory[] =
        managesAFund || category === 'loan_managers' ? [] : ['loan_managers']

    const inviteCount = cashboxData?.results?.length ?? 0
    const unseenCount = data?.pages[0]?.unseen_count ?? 0
    const awaitingCount =
        threads.data?.filter((thread) => thread.awaiting_reply).length ?? 0
    const filtered = Boolean(period || category)

    // react-query 3 has no `isFetchNextPageError`: a failed next page leaves
    // the loaded pages in place and flips the whole query to `error`.
    const loaded = Boolean(data)
    const firstPageFailed = isError && !loaded
    const nextPageFailed = isError && loaded

    // --- filter state (in the URL) ----------------------------------------
    const setParam = useCallback(
        (key: string, value: string | undefined) => {
            setParams(
                (prev) => {
                    const next = new URLSearchParams(prev)
                    if (value) next.set(key, value)
                    else next.delete(key)
                    return next
                },
                { replace: true }
            )
        },
        [setParams]
    )

    const onPeriod = (next: TInboxPeriod | undefined) => {
        posthog.capture('messages_filter_changed', {
            filter: 'period',
            period: next ?? 'all',
            category: category ?? 'all',
        })
        setParam('period', next)
    }
    const onCategory = (next: TInboxCategory | undefined) => {
        posthog.capture('messages_filter_changed', {
            filter: 'category',
            period: period ?? 'all',
            category: next ?? 'all',
        })
        setParam('category', next)
    }
    const clearFilters = () => {
        posthog.capture('messages_filter_changed', {
            filter: 'clear',
            period: 'all',
            category: 'all',
        })
        setParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                next.delete('period')
                next.delete('category')
                return next
            },
            { replace: true }
        )
    }
    const onTab = (next: Tab) => {
        if (next === tab) return
        posthog.capture('messages_tab_changed', {
            tab: next,
            awaiting_reply_count: awaitingCount,
        })
        setParam('tab', next === 'loans' ? 'loans' : undefined)
    }

    // --- mark seen --------------------------------------------------------
    const newestUnseenId = useMemo(
        () =>
            messages.reduce(
                (max, m) => (!m.is_seen && m.id > max ? m.id : max),
                0
            ),
        [messages]
    )
    useEffect(() => {
        if (!newestUnseenId) return
        const timer = window.setTimeout(() => {
            makeMessagesSeen(newestUnseenId)
                .then(() => {
                    // The server marked this id *and everything older* — in
                    // every filter, and on every other device.
                    markSeenInCache(queryClient, newestUnseenId)
                    queryClient.invalidateQueries(UNSEEN_COUNT_KEY)
                })
                .catch(() => {
                    // Not worth a toast: the messages were read either way,
                    // and the next visit simply tries again.
                })
        }, MARK_SEEN_DELAY_MS)
        return () => window.clearTimeout(timer)
    }, [newestUnseenId, queryClient])

    // --- analytics --------------------------------------------------------
    const viewedRef = useRef(false)
    useEffect(() => {
        if (viewedRef.current || !data) return
        viewedRef.current = true
        posthog.capture('messages_viewed', {
            tab,
            message_count: messages.length,
            unread_count: unseenCount,
            invite_count: cashboxData ? inviteCount : null,
            period: period ?? 'all',
            category: category ?? 'all',
        })
        // Once per visit, on the first page only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    // --- infinite scroll --------------------------------------------------
    const sentinel = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        const node = sentinel.current
        if (!node || !hasNextPage || tab !== 'inbox') return
        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0]?.isIntersecting &&
                    !isFetchingNextPage &&
                    !nextPageFailed
                )
                    fetchNextPage()
            },
            // Ask for the next page a screen early, so the member rarely
            // sees it load.
            { rootMargin: '0px 0px 600px 0px' }
        )
        observer.observe(node)
        return () => observer.disconnect()
    }, [hasNextPage, isFetchingNextPage, nextPageFailed, fetchNextPage, tab])

    // --- actions ----------------------------------------------------------
    const openMessage = useCallback(
        (message: IInboxMessage, target: MessageTarget) => {
            const destination = target.destination
            if (!destination) return
            posthog.capture('message_opened', {
                action: message.action,
                category: message.category,
                kind: target.kind,
                box_resolved: Boolean(target.boxName),
                was_unread: freshRef.current.has(message.id),
            })
            if (destination.type === 'route') {
                navigate(destination.path)
                return
            }
            const { href } = destination
            if (href.startsWith('tel:')) {
                window.location.href = href
                return
            }
            // A link to ourselves stays inside the app instead of opening a
            // second copy of it in a new tab.
            const url = new URL(href)
            if (url.origin === window.location.origin) {
                navigate(`${url.pathname}${url.search}${url.hash}`)
                return
            }
            window.open(href, '_blank', 'noopener,noreferrer')
        },
        [navigate]
    )

    const openMenu = useCallback((message: IInboxMessage) => {
        setMenuFor(message)
        setMenuOpen(true)
    }, [])

    const confirmDelete = async () => {
        if (!menuFor || deleting) return
        const message = menuFor
        setDeleting(true)
        try {
            await deleteMessage(message.id)
            setHidden((prev) => new Set(prev).add(message.id))
            removeFromCache(queryClient, message.id)
            if (!message.is_seen)
                queryClient.invalidateQueries(UNSEEN_COUNT_KEY)
            posthog.capture('message_deleted', {
                action: message.action,
                category: message.category,
                kind: targets.get(message.id)?.kind,
            })
            setMenuOpen(false)
        } catch {
            toast.error('حذف پیام انجام نشد. دوباره تلاش کنید.')
        } finally {
            setDeleting(false)
        }
    }

    const openInvites = () => {
        posthog.capture('messages_invites_opened', { count: inviteCount })
        navigate('/cashbox/invitations')
    }

    const openThread = (thread: ILoanThread) => {
        posthog.capture('loan_thread_opened', {
            awaiting_reply: thread.awaiting_reply,
            is_mine: thread.is_mine,
            count: thread.count,
            state: thread.state,
        })
        navigate(threadPath(thread))
    }

    // --- render -----------------------------------------------------------
    return (
        <>
            <Header title="پیام‌ها" />
            <SideBar>
                <SideBarItems />
            </SideBar>

            <div className="min-h-screen bg-canvas">
                <div className="mx-auto flex max-w-2xl flex-col gap-3 px-3 pb-8 pt-3">
                    {inviteCount > 0 && (
                        <button
                            type="button"
                            onClick={openInvites}
                            className="flex w-full items-center gap-3 rounded-card border border-divider bg-white p-3.5 text-right shadow-badge transition-colors animate-fade-in active:bg-sunken md:p-4"
                        >
                            <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand-deep md:h-11 md:w-11">
                                <LuUserPlus className="h-5 w-5" />
                                <span className="absolute -left-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-danger px-1 text-[11px] font-bold leading-none text-white ring-2 ring-white">
                                    {faPlain.format(inviteCount)}
                                </span>
                            </span>
                            <span className="flex-1 overflow-hidden">
                                <span className="block text-[13px] font-bold text-heading md:text-sm">
                                    دعوت‌ها و درخواست‌های عضویت
                                </span>
                                <span className="mt-0.5 block text-[11px] text-text-secondary md:text-xs">
                                    {faPlain.format(inviteCount)} مورد منتظر
                                    پاسخ شماست
                                </span>
                            </span>
                            <FiChevronLeft className="h-5 w-5 shrink-0 text-text-muted" />
                        </button>
                    )}

                    <Tabs
                        tab={tab}
                        unseenCount={unseenCount}
                        awaitingCount={awaitingCount}
                        onTab={onTab}
                    />

                    <AnimatePresence mode="wait" initial={false}>
                        {tab === 'inbox' ? (
                            <motion.div
                                key="inbox"
                                initial={{ opacity: 0, x: -12 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 12 }}
                                transition={{ duration: 0.2 }}
                                className="flex flex-col gap-3"
                            >
                                <FilterBar
                                    period={period}
                                    category={category}
                                    onPeriod={onPeriod}
                                    onCategory={onCategory}
                                    hidden={hiddenCategories}
                                />
                                <FilterSummary
                                    period={period}
                                    category={category}
                                    onClear={clearFilters}
                                />

                                {isLoading && (
                                    <>
                                        <MessageCardSkeleton />
                                        <MessageCardSkeleton />
                                        <MessageCardSkeleton />
                                    </>
                                )}

                                {firstPageFailed && (
                                    <SectionError
                                        message="دریافت پیام‌ها انجام نشد"
                                        onRetry={refetch}
                                    />
                                )}

                                {loaded &&
                                    messages.length === 0 &&
                                    (filtered ? (
                                        <NoMatches onClear={clearFilters} />
                                    ) : (
                                        <EmptyInbox />
                                    ))}

                                {/* Keyed by the filter, so a new list plays its
                                    entrance instead of morphing into place. */}
                                <div
                                    key={`${period ?? 'all'}-${
                                        category ?? 'all'
                                    }`}
                                    className="flex flex-col gap-3"
                                >
                                    {days.map((day) => (
                                        <section
                                            key={day.key}
                                            className="flex flex-col gap-3"
                                        >
                                            <div className="pointer-events-none sticky top-14 z-10 flex justify-center pt-1">
                                                <span className="rounded-full border border-divider bg-white/95 px-3 py-1 text-[11px] text-text-secondary shadow-badge backdrop-blur md:text-xs">
                                                    {dayLabel(day.key)}
                                                </span>
                                            </div>
                                            <AnimatePresence initial={false}>
                                                {day.messages.map(
                                                    (message, index) => (
                                                        <MessageCard
                                                            key={message.id}
                                                            message={message}
                                                            target={
                                                                targets.get(
                                                                    message.id
                                                                ) ?? {
                                                                    kind: 'info',
                                                                }
                                                            }
                                                            fresh={freshRef.current.has(
                                                                message.id
                                                            )}
                                                            index={index}
                                                            onOpen={openMessage}
                                                            onMenu={openMenu}
                                                        />
                                                    )
                                                )}
                                            </AnimatePresence>
                                        </section>
                                    ))}
                                </div>

                                {isFetchingNextPage && <MessageCardSkeleton />}

                                {nextPageFailed && !isFetchingNextPage && (
                                    <SectionError
                                        message="دریافت پیام‌های قدیمی‌تر انجام نشد"
                                        onRetry={() => fetchNextPage()}
                                    />
                                )}

                                {loaded &&
                                    !nextPageFailed &&
                                    messages.length > 0 &&
                                    !hasNextPage &&
                                    !isFetchingNextPage && (
                                        <div className="flex items-center gap-3 px-6 pt-1 text-[11px] text-text-muted md:text-xs">
                                            <span className="h-px flex-1 bg-stroke" />
                                            پیام قدیمی‌تری نیست
                                            <span className="h-px flex-1 bg-stroke" />
                                        </div>
                                    )}

                                <div ref={sentinel} aria-hidden />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="loans"
                                initial={{ opacity: 0, x: 12 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -12 }}
                                transition={{ duration: 0.2 }}
                                className="flex flex-col gap-3"
                            >
                                <LoanThreads
                                    threads={threads.data}
                                    isLoading={threads.isLoading}
                                    isError={threads.isError}
                                    onRetry={threads.refetch}
                                    onOpen={openThread}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <CustomBottomSheet
                isOpen={menuOpen}
                onDismiss={() => !deleting && setMenuOpen(false)}
            >
                {menuFor && (
                    <div className="pb-1">
                        <p className="text-center text-base font-bold text-heading">
                            حذف این پیام؟
                        </p>
                        <p className="mt-3 line-clamp-3 whitespace-pre-line break-words rounded-card border border-divider bg-sunken p-3 text-[13px] leading-6 text-text-body md:text-sm md:leading-7">
                            {menuFor.body}
                        </p>
                        <p className="mt-2 text-center text-xs text-text-secondary">
                            پیام حذف‌شده قابل بازگرداندن نیست.
                        </p>
                        <button
                            type="button"
                            onClick={confirmDelete}
                            disabled={deleting}
                            aria-busy={deleting}
                            className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-card bg-danger text-sm font-bold text-white transition-opacity active:opacity-90 disabled:opacity-60"
                        >
                            <LuTrash2 className="h-[18px] w-[18px]" />
                            {deleting ? 'در حال حذف…' : 'حذف پیام'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setMenuOpen(false)}
                            disabled={deleting}
                            className="mt-2 h-12 w-full rounded-card text-sm font-bold text-text-secondary transition-colors active:bg-divider"
                        >
                            انصراف
                        </button>
                    </div>
                )}
            </CustomBottomSheet>
        </>
    )
}

/**
 * «همهٔ پیام‌ها» | «گفت‌وگوی وام». The green pill slides between the two, and
 * each side carries its own count: unread messages, and threads waiting on a
 * reply from this member.
 */
function Tabs({
    tab,
    unseenCount,
    awaitingCount,
    onTab,
}: {
    tab: Tab
    unseenCount: number
    awaitingCount: number
    onTab: (tab: Tab) => void
}) {
    const options: { key: Tab; label: string; count: number; Icon: typeof LuInbox }[] =
        [
            {
                key: 'inbox',
                label: 'همهٔ پیام‌ها',
                count: unseenCount,
                Icon: LuInbox,
            },
            {
                key: 'loans',
                label: 'گفت‌وگوی وام',
                count: awaitingCount,
                Icon: LuMessagesSquare,
            },
        ]
    return (
        <div
            role="tablist"
            aria-label="نوع نمایش پیام‌ها"
            className="grid grid-cols-2 gap-1 rounded-card border border-divider bg-white p-1 shadow-badge"
        >
            {options.map(({ key, label, count, Icon }) => {
                const active = tab === key
                return (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onTab(key)}
                        className={classNames(
                            'relative flex h-10 items-center justify-center gap-1.5 rounded-xl text-[12px] font-bold transition-colors duration-200 md:h-11 md:text-sm',
                            active
                                ? 'text-white'
                                : 'text-text-secondary active:bg-canvas'
                        )}
                    >
                        {active && (
                            <motion.span
                                layoutId="messages-tab-pill"
                                className="absolute inset-0 rounded-xl bg-green-1 shadow-fab"
                                transition={{
                                    type: 'spring',
                                    stiffness: 480,
                                    damping: 36,
                                }}
                            />
                        )}
                        <span className="relative flex items-center gap-1.5">
                            <Icon className="h-4 w-4" aria-hidden />
                            {label}
                            {count > 0 && (
                                <span
                                    className={classNames(
                                        'flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none',
                                        active
                                            ? 'bg-white/25 text-white'
                                            : 'bg-danger text-white'
                                    )}
                                >
                                    {badgeText(count, (n) =>
                                        faPlain.format(n)
                                    )}
                                </span>
                            )}
                        </span>
                    </button>
                )
            })}
        </div>
    )
}

function EmptyInbox() {
    return (
        <div className="flex flex-col items-center px-8 pt-14 text-center animate-fade-in">
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-brand-soft md:h-24 md:w-24">
                <span className="absolute inset-3 rounded-full bg-white/70" />
                <LuInbox className="relative h-9 w-9 text-green-1 md:h-10 md:w-10" />
            </div>
            <p className="mt-5 text-[15px] font-bold text-heading md:text-base">
                هنوز پیامی ندارید
            </p>
            <p className="mt-2 text-[13px] leading-6 text-text-secondary md:text-sm md:leading-7">
                یادآوری پرداخت‌ها، دعوت به صندوق‌ها و خبرهای صندوق‌هایتان اینجا
                جمع می‌شود.
            </p>
        </div>
    )
}

/** An empty list with a filter on is a dead end unless the way out is here. */
function NoMatches({ onClear }: { onClear: () => void }) {
    return (
        <div className="flex flex-col items-center px-8 pt-10 text-center animate-fade-in">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-divider md:h-20 md:w-20">
                <LuSearchX className="h-7 w-7 text-gray-1 md:h-8 md:w-8" />
            </div>
            <p className="mt-4 text-[15px] font-bold text-heading md:text-base">
                پیامی با این فیلترها نیست
            </p>
            <p className="mt-2 text-[13px] leading-6 text-text-secondary md:text-sm md:leading-7">
                بازهٔ زمانی را بازتر کنید یا نوع دیگری را ببینید.
            </p>
            <button
                type="button"
                onClick={onClear}
                className="mt-4 h-10 rounded-full bg-green-1 px-5 text-[13px] font-bold text-white shadow-fab transition-opacity active:opacity-90 md:text-sm"
            >
                نمایش همهٔ پیام‌ها
            </button>
        </div>
    )
}
